import { createApi, retry } from "@reduxjs/toolkit/query/react";
import { createFlatBufferBaseQuery } from "../../flatbuffers/api/flatBufferBaseQuery";
import { formatUuidT } from "../../flatbuffers/display";
import fetchAllPages from "../../utils/fetchAllPages";

const maxRetries = 2;

const baseQuery = retry(createFlatBufferBaseQuery(""), { maxRetries });

const config = typeof window !== "undefined" ? window.RUNTIME_CONFIG : undefined;

const REACT_APP_LIFT_NET_LOCAL_API_OFFLINE = config?.REACT_APP_LIFT_NET_LOCAL_API_OFFLINE || "";

const normalizeGridId = (idGrid: any): string => {
  if (typeof idGrid === "bigint") return idGrid.toString();
  if (typeof idGrid === "string" || typeof idGrid === "number") return String(idGrid);
  return formatUuidT(idGrid);
};

export const monitoringAPI = createApi({
  reducerPath: "monitoringAPI",
  baseQuery,
  keepUnusedDataFor: 0,
  tagTypes: ["tagGetOrganizationCascader", "tagGetFavoriteGrids"],
  refetchOnMountOrArgChange: true,
  refetchOnReconnect: true,
  endpoints: (builder) => ({
    //* *********************************************************** */
    //* GET ALL ORGANIZATIONS AND ITS CASCADER DATA */
    getOrganizationsCascader: builder.query({
      query: (body: any) => ({
        url: `${process.env.REACT_APP_LIFT_NET_CLOUD_API_ONLINE}/monitor-view/`,
        headers: { authorization: body.token },
      }),
      providesTags: ["tagGetOrganizationCascader"],
    }),
    //* *********************************************************** */
    //* GET FAVORITE GRIDS */
    getFavoriteGrids: builder.query({
      queryFn: async (body: any, _queryApi, _extraOptions, _baseQuery) =>
        fetchAllPages(
          _baseQuery,
          `${process.env.REACT_APP_LIFT_NET_CLOUD_API_ONLINE}/monitor-grid/?&page=1&limit=500`,
          { authorization: body.token },
        ),
      providesTags: ["tagGetFavoriteGrids"],
    }),
    //* *********************************************************** */
    //* GET FAVORITE GRID BY ID */
    getFavoriteGrid: builder.query({
      query: (body: any) => ({
        url: `${process.env.REACT_APP_LIFT_NET_CLOUD_API_ONLINE}/monitor-grid/${normalizeGridId(
          body.idGrid,
        )}`,
        headers: { authorization: body.token },
      }),
      serializeQueryArgs: ({ endpointName, queryArgs }) =>
        `${endpointName}-${normalizeGridId(queryArgs?.idGrid)}`,
      providesTags: [],
    }),
    //* *********************************************************** */
    //* GET FAVORITE GRIDS BY USER */
    getFavoriteGridsByUser: builder.query({
      query: (body: any) => ({
        url: `${process.env.REACT_APP_LIFT_NET_CLOUD_API_ONLINE}/monitor-grid/by-user/${body.idUser}`,
        headers: { authorization: body.token },
      }),
      providesTags: ["tagGetFavoriteGrids"],
    }),
    //* *********************************************************** */
    //* GET FAVORITE GRIDS BY USER */
    getFavoriteGridsByOrganization: builder.query({
      query: (body: any) => ({
        url: `${process.env.REACT_APP_LIFT_NET_CLOUD_API_ONLINE}/monitor-grid/by-organization/${body.idOrganization}`,
        headers: { authorization: body.token },
      }),
      providesTags: ["tagGetFavoriteGrids"],
    }),
    //* *********************************************************** */
    //* POST FAVORITE GRIDS */
    postFavoriteGrids: builder.mutation({
      query: (body: any) => ({
        url: `${process.env.REACT_APP_LIFT_NET_CLOUD_API_ONLINE}/monitor-grid/`,
        method: "POST",
        body: body.body,
        headers: { authorization: body.token },
      }),
      invalidatesTags: ["tagGetFavoriteGrids"],
    }),
    //* *********************************************************** */
    //* PUT FAVORITE GRIDS */
    putFavoriteGrids: builder.mutation({
      query: (body: any) => ({
        url: `${process.env.REACT_APP_LIFT_NET_CLOUD_API_ONLINE}/monitor-grid/${body.id}`,
        method: "PUT",
        headers: { authorization: body.token },
        body: body.body,
      }),
      invalidatesTags: ["tagGetFavoriteGrids"],
    }),
    //* *********************************************************** */
    //* DELETE FAVORITE GRIDS */
    deleteFavoriteGrids: builder.mutation({
      query: (body: any) => ({
        url: `${process.env.REACT_APP_LIFT_NET_CLOUD_API_ONLINE}/monitor-grid/${body.id}`,
        method: "DELETE",
        headers: { authorization: body.token },
      }),
      invalidatesTags: ["tagGetFavoriteGrids"],
    }),
    //* *********************************************************** */
    //* POST SEND COMMANDS */
    postMonitoringCommand: builder.mutation({
      query: (body: any) => ({
        url: `${
          process.env.REACT_APP_EDGE_SERVER_CONNECTION === "OFFLINE"
            ? REACT_APP_LIFT_NET_LOCAL_API_OFFLINE
            : process.env.REACT_APP_LIFT_NET_CLOUD_API_ONLINE
        }${
          process.env.REACT_APP_EDGE_SERVER_CONNECTION === "OFFLINE"
            ? "/monitoring/command"
            : "/monitor-grid/command"
        }${process.env.REACT_APP_EDGE_SERVER_CONNECTION === "OFFLINE" ? "" : `/${body.serverId}`}`,
        method: "POST",
        body: body.body,
        headers: { authorization: body.token },
      }),
      invalidatesTags: [],
    }),
    //* *********************************************************** */
    //* POST REFRESH ALL OFFLINE */
    postMonitoringCommandRefreshAllOffline: builder.mutation({
      query: (body: any) => ({
        url: `${REACT_APP_LIFT_NET_LOCAL_API_OFFLINE}/monitoring/refresh-command/${body.serverId}`,
        method: "POST",
        body: body.body,
        headers: { authorization: body.token },
      }),
      invalidatesTags: [],
    }),
    //* *********************************************************** */
    //* POST EMULATOR COMMAND */
    postEmulatorCommand: builder.mutation({
      query: (body: any) => ({
        url: `${process.env.REACT_APP_WEB_SOCKET_EMULATOR}/monitor-grid/command`,
        method: "POST",
        body: body.body,
      }),
      invalidatesTags: [],
    }),
    //* *********************************************************** */
    //* GET BANK MESSAGES */
    getBankMessages: builder.query({
      query: (body: any) => ({
        url: `${process.env.REACT_APP_LIFT_NET_CLOUD_API_ONLINE}/banks/messages/${body.bankId}`,
        headers: { authorization: body.token },
      }),
      providesTags: [],
    }),
    //* *********************************************************** */
  }),
});

export const {
  useLazyGetOrganizationsCascaderQuery,
  useLazyGetFavoriteGridsQuery,
  useLazyGetFavoriteGridQuery,
  useLazyGetFavoriteGridsByUserQuery,
  useLazyGetFavoriteGridsByOrganizationQuery,
  usePostFavoriteGridsMutation,
  usePutFavoriteGridsMutation,
  useDeleteFavoriteGridsMutation,
  usePostMonitoringCommandMutation,
  usePostMonitoringCommandRefreshAllOfflineMutation,
  usePostEmulatorCommandMutation,
  useLazyGetBankMessagesQuery,
} = monitoringAPI;
